import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaEnvelope,
  FaPaperPlane,
  FaShieldAlt,
} from "react-icons/fa";
import { forgotPassword } from "./../../services/passwordServices";
import backgroundImage from "./illustration_login.webp";
import useScrollToTop from "./../../hooks/useScollToTop";

function ForgotPasswordPage() {
  useScrollToTop();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrorMessage("");

    try {
      setIsSubmitting(true);

      const result = await forgotPassword(email);

      if (result.success) {
        setMessage(
          "Si un compte avec cet email existe, un lien de réinitialisation a été envoyé.",
        );
        setTimeout(() => navigate("/login"), 5000);
      } else {
        setErrorMessage(
          result.message ||
            "Impossible d'envoyer le lien de réinitialisation pour le moment.",
        );
      }
    } catch (error) {
      setErrorMessage(
        "Impossible d'envoyer le lien de réinitialisation pour le moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-slate-950"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />

      <div className="relative z-10 flex min-h-screen items-start justify-center px-3 py-3 sm:px-4 sm:py-5 md:items-center md:py-8">
        <div className="w-full max-w-[980px] overflow-hidden rounded-2xl border border-white/10 bg-white/95 shadow-2xl dark:border-slate-700/60 dark:bg-slate-900/95 md:rounded-3xl">
          <div className="grid md:grid-cols-[0.88fr_1.12fr]">
            {/* Colonne branding desktop */}
            <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white lg:p-8">
              <div>
                <p className="mb-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Club Présence Pro
                </p>

                <h1 className="text-2xl font-black leading-tight lg:text-3xl">
                  Réinitialisez votre mot de passe en toute simplicité
                </h1>

                <p className="mt-5 max-w-md text-sm leading-6 text-white/80">
                  Saisissez votre adresse email et nous vous enverrons un lien
                  pour choisir un nouveau mot de passe.
                </p>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Procédure simple et rapide.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Lien de réinitialisation envoyé par email.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Vérifiez aussi vos spams ou courriers indésirables.
                  </p>
                </div>
              </div>
            </div>

            {/* Colonne contenu */}
            <div className="p-4 sm:p-6 md:p-7 lg:p-8">
              <div className="mx-auto w-full max-w-none">
                {/* Intro mobile */}
                <div className="mb-5 md:hidden">
                  <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4 text-white shadow-lg backdrop-blur-sm">
                    <p className="mb-2 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                      Club Présence Pro
                    </p>

                    <h1 className="text-xl font-black leading-tight">
                      Réinitialisez votre mot de passe en toute simplicité
                    </h1>

                    <p className="mt-2 text-sm leading-6 text-white/80">
                      Saisissez votre email et recevez un lien pour définir un
                      nouveau mot de passe.
                    </p>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Procédure simple et rapide.
                        </p>
                      </div>

                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Pensez à vérifier vos spams.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5 text-center md:text-left">
                  <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 sm:text-3xl">
                    Mot de passe oublié
                  </h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    Entrez votre email pour recevoir un lien de
                    réinitialisation.
                  </p>
                </div>

                {message && (
                  <div
                    className="mb-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300"
                    role="alert"
                  >
                    {message}
                  </div>
                )}

                {errorMessage && (
                  <div
                    className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                    role="alert"
                  >
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20 dark:bg-blue-500">
                        <FaShieldAlt />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          Réinitialisation sécurisée
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Nous vous envoyons un lien à l’adresse associée au
                          compte.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Email
                      </label>
                      <div className="relative">
                        <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                        <input
                          type="email"
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                          placeholder="Entrez votre email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300">
                    Merci de vérifier également les spams de votre messagerie
                    pour être sûr de recevoir le lien de réinitialisation.
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-blue-500 dark:hover:bg-blue-600"
                  >
                    <FaPaperPlane />
                    {isSubmitting
                      ? "Envoi en cours..."
                      : "Envoyer le lien de réinitialisation"}
                  </button>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <Link
                        to="/login"
                        className="font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Retour à la connexion
                      </Link>

                      <button
                        type="button"
                        onClick={() => navigate("/register")}
                        className="text-left font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 sm:text-right"
                      >
                        Créer un compte
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
